/* =============================================================================
 * Signing in without an email address
 * -----------------------------------------------------------------------------
 * A site worker has no mailbox. The form insisted on one anyway — `testdriver1`
 * would not save, `testdriver1@noor1.com` would — so somebody typing a name for
 * a driver had to invent an address that reaches nobody and then remember what
 * they invented. That address was doing no work: credentials are handed over in
 * person, never mailed.
 *
 * Supabase Auth has no username login. Every account needs an email or a phone,
 * and neither of those is optional in the way this needs. So a username becomes
 * an address here, deterministically, in one function that both the browser and
 * the edge function call — and never in a lookup table, because a table mapping
 * names to accounts is a table anybody can ask whether a name exists.
 *
 *   testdriver1            ->  testdriver1@users.noorone.ae
 *   ahmed@qemat.ae         ->  ahmed@qemat.ae
 *
 * users.noorone.ae has no MX record and receives nothing. It is a spelling of
 * "this login has no email", not an address, and the screens show only the part
 * before the @.
 *
 * WHAT A USERNAME IS, AND IS NOT
 * -----------------------------------------------------------------------------
 * A shortcut. Since 0128 every account has an email address and can sign in
 * with it, so this is only something shorter to type at a gate on a cold
 * morning — never the identity itself.
 *
 * Which is what lets it be unique per company rather than across the platform
 * (0129). Two companies may both have a `driver1`, and resolve-login tells them
 * apart by trying the password against each. An identity could not be ambiguous
 * like that; a shortcut can, because there is always the address behind it.
 *
 * No spaces, ever. It becomes the local part of an address on an account made
 * before addresses were required, and the grammar there has no room for one —
 * and it is typed by hand at a gate, where a space is a thing half of them will
 * get wrong.
 * ========================================================================== */

/** Where a login with no mailbox lives. Never delivered to. */
export const LOGIN_DOMAIN = 'users.noorone.ae';

/* Deliberately narrow. A username ends up in an email address, so anything
   the address grammar dislikes has to be refused here rather than discovered
   as an opaque provider error three screens later. Lowercase because the
   address is lowercased anyway and `Driver1` finding no account while
   `driver1` works is a support call nobody can diagnose. */
const USERNAME = /^[a-z0-9][a-z0-9._-]{1,30}[a-z0-9]$/;

/* Not a validator for the world's email addresses — just enough to catch the
   typo before it becomes a provider error. */
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** True when what was typed is a bare name rather than an address. */
export function isUsername(identifier: string): boolean {
  return !identifier.includes('@');
}

/**
 * True when a bare name is one this system will accept.
 *
 * Separate from loginEmail() because the two questions diverged: the login
 * screen needs to know whether to send a name to resolve-login, and no longer
 * needs an address built from it. Creating an account still does.
 */
export function isValidUsername(name: string): boolean {
  return USERNAME.test(name.trim().toLowerCase());
}

/** True when something with an @ in it is worth trying to sign in with. */
export function isValidEmail(address: string): boolean {
  return EMAIL.test(address.trim().toLowerCase());
}

/**
 * What to hand to Supabase for something somebody typed into a login or user
 * form. Null when it is neither a usable username nor a plausible address —
 * the caller says why, because the two cases need different sentences.
 */
export function loginEmail(identifier: string): string | null {
  const typed = identifier.trim().toLowerCase();
  if (!typed) return null;

  if (isUsername(typed)) {
    return USERNAME.test(typed) ? `${typed}@${LOGIN_DOMAIN}` : null;
  }

  return EMAIL.test(typed) ? typed : null;
}

/**
 * The other direction, for anywhere an account is shown. A synthesised address
 * is displayed as the name somebody types, because that is what it is — showing
 * `driver1@users.noorone.ae` in a list invites somebody to write to it.
 */
export function displayLogin(email: string | null | undefined): string {
  if (!email) return '—';
  const suffix = `@${LOGIN_DOMAIN}`;
  return email.endsWith(suffix) ? email.slice(0, -suffix.length) : email;
}
